import type { ArmMetric, ArmResults } from "../types/outreach.types"

const ROWS: { key: ArmMetric; label: string; of?: ArmMetric }[] = [
  { key: "patients", label: "Patients" },
  { key: "texted", label: "Texted", of: "patients" },
  { key: "delivered", label: "Delivered", of: "texted" },
  { key: "replied", label: "Replied", of: "texted" },
  { key: "stop", label: "STOP", of: "texted" },
  { key: "called", label: "Called", of: "patients" },
  { key: "answered", label: "Answered", of: "called" },
  { key: "consented_phone", label: "Consented by phone", of: "patients" },
  { key: "consented_online", label: "Consented online", of: "patients" },
  { key: "consented", label: "Consented", of: "patients" },
]

const armLabel = (arm: string) => (arm === "none" ? "No text" : `Text ${arm}`)

/** The per-arm table (also used by APCM Ops: program arms and the calls dashboard). */
export function ArmResultsTable({ results }: { results: ArmResults }) {
  const arms = Object.keys(results.arms ?? {})
  const unknown = arms.reduce((n, a) => n + (results.arms[a].delivery_unknown || 0), 0)
  if (arms.length === 0) return <p className="text-sm text-muted-foreground">No reminder texts yet.</p>
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1 pr-3 font-normal" />
              {arms.map((arm) => (
                <th key={arm} className="py-1 pr-3 text-right font-medium">
                  {armLabel(arm)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map(({ key, label, of }) => (
              <tr key={key} className="border-t" title={results.definitions?.[key]}>
                <td className="py-1 pr-3 text-muted-foreground">{label}</td>
                {arms.map((arm) => {
                  const n = results.arms[arm][key] || 0
                  const base = of ? results.arms[arm][of] || 0 : 0
                  return (
                    <td key={arm} className="py-1 pr-3 text-right tabular-nums">
                      {n}
                      {of && base > 0 && (
                        <span className="ml-1 text-xs text-muted-foreground">{Math.round((n / base) * 100)}%</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Each patient always gets the same version (picked from their id). Consents count when they come after
        the patient's first tagged text or call.
        {unknown > 0 && ` ${unknown} text(s) have no delivery report from Twilio yet.`}
      </p>
    </div>
  )
}
